import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalBody,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	HStack,
	VStack,
	Box,
	Text,
	Badge,
	Icon,
	Kbd,
	Spinner,
	Divider,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiCornerDownLeft, FiLock } from 'react-icons/fi';
import { useFetchItemsQuery } from 'api/apiSlice';
import { useFinanceUI } from './ui';
import { useDebouncedValue } from './hooks';
import { fmtMoney, fmtDate, statusLabel } from './ui';

/**
 * The global finance omnibox (PR-44).
 *
 * Opens on ⌘K / Ctrl-K, navigates with the arrow keys, opens with Enter. The
 * point is to get to a document from anywhere without knowing which module it
 * lives in — so the results are grouped by type but ordered by relevance, and
 * an exact document-number match lands first.
 *
 * Results only ever contain what the user could already open: the server
 * filters by each document type's own permission. What was NOT searched is
 * shown too, so an empty result never silently means "you lack permission".
 */
const SearchOmnibox = ({ isOpen, onClose }) => {
	const ui = useFinanceUI();
	const navigate = useNavigate();
	const [term, setTerm] = useState('');
	const [cursor, setCursor] = useState(0);
	const inputRef = useRef(null);
	const debounced = useDebouncedValue(term, 250);

	const q = useFetchItemsQuery(
		{ path: 'finance-core/search', params: { q: debounced, limit: 5 } },
		{ skip: !isOpen || debounced.trim().length < 2 },
	);
	const data = q.data?.data;
	const results = useMemo(() => data?.results || [], [data]);

	useEffect(() => { if (isOpen) { setTerm(''); setCursor(0); } }, [isOpen]);
	useEffect(() => { setCursor(0); }, [debounced]);

	const open = (row) => {
		if (!row) return;
		onClose();
		navigate(row.route);
	};

	const onKeyDown = (e) => {
		if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
		else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
		else if (e.key === 'Enter') { e.preventDefault(); open(results[cursor]); }
	};

	const skipped = data?.skippedTypes || [];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='2xl' initialFocusRef={inputRef} scrollBehavior='inside'>
			<ModalOverlay backdropFilter='blur(4px)' bg='bg.overlay' />
			<ModalContent bg={ui.surface} borderWidth='1px' borderColor={ui.border} borderRadius='xl' mt='10vh'>
				<ModalBody p={0}>
					<InputGroup size='lg'>
						<InputLeftElement pointerEvents='none' h='56px'>
							<Icon as={FiSearch} color={ui.iconMuted} />
						</InputLeftElement>
						<Input
							ref={inputRef}
							h='56px'
							border='none'
							_focus={{ boxShadow: 'none' }}
							placeholder='Invoice number, supplier, amount…'
							aria-label='Search finance documents'
							value={term}
							onChange={(e) => setTerm(e.target.value)}
							onKeyDown={onKeyDown}
							color={ui.textPrimary}
						/>
						<InputRightElement h='56px' w='auto' pr={4}>
							{q.isFetching ? (
								<Spinner size='sm' color={ui.textMuted} />
							) : (
								<Kbd
									fontSize='xs'
									cursor='pointer'
									aria-label='Close search'
									onClick={onClose}
									_hover={{ borderColor: ui.accent, color: ui.textPrimary }}
								>
									esc
								</Kbd>
							)}
						</InputRightElement>
					</InputGroup>

					<Divider borderColor={ui.borderSubtle} />

					{term.trim().length < 2 && (
						<Box px={5} py={6}>
							<Text fontSize='sm' color={ui.textMuted}>
								Type at least two characters. A document number jumps straight to it; a name or an amount searches everywhere you have access.
							</Text>
						</Box>
					)}

					{term.trim().length >= 2 && !q.isFetching && results.length === 0 && (
						<Box px={5} py={6}>
							<Text fontSize='sm' color={ui.textPrimary}>Nothing found for “{term}”.</Text>
							{skipped.length > 0 && (
								<Text fontSize='xs' color={ui.textMuted} mt={2}>
									{skipped.length} document type{skipped.length === 1 ? '' : 's'} were not searched because you do not have access to them
									{skipped.length <= 4 ? `: ${skipped.map((s) => s.label).join(', ')}` : ''}.
								</Text>
							)}
						</Box>
					)}

					{results.length > 0 && (
						<VStack align='stretch' spacing={0} maxH='50vh' overflowY='auto'>
							{results.map((row, i) => (
								<Box
									key={`${row.type}-${row.id}`}
									px={5}
									py={3}
									cursor='pointer'
									bg={i === cursor ? ui.surfaceSubtle : 'transparent'}
									borderLeftWidth='2px'
									borderLeftColor={i === cursor ? ui.accent : 'transparent'}
									onMouseEnter={() => setCursor(i)}
									onClick={() => open(row)}
									role='button'
									tabIndex={-1}
									aria-label={`${row.typeLabel}: ${row.title}`}
								>
									<HStack justify='space-between' align='flex-start'>
										<Box flex='1' minW={0}>
											<HStack spacing={2}>
												<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary} noOfLines={1}>{row.title}</Text>
												<Badge colorScheme='gray' variant='subtle' fontSize='0.5em'>{row.typeLabel}</Badge>
												{row.status && <Badge colorScheme='blue' variant='subtle' fontSize='0.5em'>{statusLabel(row.status)}</Badge>}
											</HStack>
											{row.subtitle && <Text fontSize='xs' color={ui.textMuted} noOfLines={1}>{row.subtitle}</Text>}
										</Box>
										<VStack align='flex-end' spacing={0} flexShrink={0}>
											{row.amount != null && (
												<Text fontSize='sm' fontWeight='semibold' color={ui.textPrimary}>{fmtMoney(row.amount)}</Text>
											)}
											{row.date && <Text fontSize='xs' color={ui.textMuted}>{fmtDate(row.date)}</Text>}
										</VStack>
									</HStack>
								</Box>
							))}
						</VStack>
					)}

					{data && (data.totalMatches > data.totalShown || skipped.length > 0) && (
						<>
							<Divider borderColor={ui.borderSubtle} />
							<HStack px={5} py={2} justify='space-between'>
								<Text fontSize='xs' color={ui.textMuted}>
									{data.totalMatches > data.totalShown
										? `Showing ${data.totalShown} of ${data.totalMatches} matches — narrow the search to see more.`
										: `${data.totalShown} match${data.totalShown === 1 ? '' : 'es'}.`}
								</Text>
								{skipped.length > 0 && (
									<HStack spacing={1}>
										<Icon as={FiLock} color={ui.textMuted} boxSize={3} />
										<Text fontSize='xs' color={ui.textMuted}>{skipped.length} type{skipped.length === 1 ? '' : 's'} hidden</Text>
									</HStack>
								)}
							</HStack>
						</>
					)}

					<Divider borderColor={ui.borderSubtle} />
					<HStack px={5} py={2} spacing={4}>
						<HStack spacing={1}>
							<Kbd fontSize='xs' cursor='pointer' aria-label='Previous result' onClick={() => setCursor((c) => Math.max(c - 1, 0))} _hover={{ borderColor: ui.accent }}>↑</Kbd>
							<Kbd fontSize='xs' cursor='pointer' aria-label='Next result' onClick={() => setCursor((c) => Math.min(c + 1, results.length - 1))} _hover={{ borderColor: ui.accent }}>↓</Kbd>
							<Text fontSize='xs' color={ui.textMuted}>navigate</Text>
						</HStack>
						<HStack
							spacing={1}
							role='button'
							aria-label='Open selected result'
							cursor={results.length ? 'pointer' : 'default'}
							onClick={() => open(results[cursor])}
							_hover={results.length ? { color: ui.textPrimary } : undefined}
						>
							<Icon as={FiCornerDownLeft} color={ui.textMuted} boxSize={3} />
							<Text fontSize='xs' color={ui.textMuted}>open</Text>
						</HStack>
					</HStack>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

/**
 * Wires ⌘K / Ctrl-K to the omnibox. Returns the props to spread onto it, so a
 * page can mount the box without owning the keyboard logic.
 */
/**
 * ⌘K for the finance omnibox.
 *
 * ★ `enabled` EXISTS BECAUSE THE LISTENER IS ON `window`. Mounting this hook
 * anywhere binds the shortcut for the whole document and calls preventDefault,
 * so a screen that merely reuses the finance page shell silently takes ⌘K away
 * from the browser and answers it with a finance search. Screens outside
 * finance pass false and the listener is never registered at all — not
 * registered-and-ignored, because preventDefault would still have fired.
 */
export function useSearchOmnibox({ enabled = true } = {}) {
	const [isOpen, setIsOpen] = useState(false);
	useEffect(() => {
		if (!enabled) return undefined;
		const onKey = (e) => {
			if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
				e.preventDefault();
				setIsOpen(true);
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [enabled]);
	return { isOpen, onOpen: () => setIsOpen(true), onClose: () => setIsOpen(false) };
}

export default SearchOmnibox;
